// src/pages/RewardsPage.js
// Dedicated $TGEN Airdrop page (Trend Genie Social theme)
// Slug target: /tgen-airdrop-live

import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  FaWallet,
  FaRegClock,
  FaTasks,
  FaCoins,
  FaShieldAlt,
  FaLink,
  FaUserCheck,
  FaBolt,
} from "react-icons/fa";

/* ============================================================
   PAGE WRAPPER
============================================================ */

const Page = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 55%, #fef6e4 100%);
  color: var(--text-color);
  padding: 3rem 0 6rem;
`;

/* ============================================================
   HERO
============================================================ */

const Hero = styled.section`
  padding: 3.5rem 10% 2.5rem;
`;

const HeroInner = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2.5rem;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Kicker = styled.p`
  color: var(--primary-color);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 0.7rem;
`;

const Title = styled(motion.h1)`
  font-size: 3.2rem;
  line-height: 1.1;
  margin: 0 0 1rem;

  @media (max-width: 520px) {
    font-size: 2.6rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.15rem;
  color: var(--text-secondary-color);
  max-width: 720px;
  line-height: 1.7;
  margin: 0 0 1.6rem;
`;

const HeroCTA = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: var(--text-color);
  font-weight: 800;
  text-decoration: none;
  transition: 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 44px rgba(90, 110, 255, 0.45);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  color: var(--text-secondary-color);
  font-weight: 800;
  text-decoration: none;
  transition: 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
  }
`;

const HeroRight = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 22px;
  padding: 1.6rem;
  backdrop-filter: blur(14px);
`;

const StatTitle = styled.div`
  font-weight: 800;
  color: var(--text-color);
  margin-bottom: 0.8rem;
  font-size: 1.05rem;
`;

const StatText = styled.div`
  color: var(--text-secondary-color);
  line-height: 1.7;
  font-size: 1rem;
`;

const StatGrid = styled.div`
  display: grid;
  gap: 0.85rem;
  margin-top: 1rem;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0.95rem;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
`;

const StatLabel = styled.div`
  color: var(--text-secondary-color);
  font-weight: 700;
`;

const StatValue = styled.div`
  color: var(--text-color);
  font-weight: 900;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 900;
  color: var(--text-color);
  background: rgba(74, 222, 128, 0.18);
  border: 1px solid rgba(74, 222, 128, 0.28);
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  word-break: break-all;
  color: var(--text-secondary-color);
`;

/* ============================================================
   SECTIONS
============================================================ */

const Section = styled.section`
  padding: 3.5rem 10%;
`;

const SectionTitle = styled.h2`
  font-size: 2.3rem;
  margin: 0 0 1rem;
  text-align: center;
`;

const SectionDesc = styled.p`
  color: var(--text-secondary-color);
  max-width: 880px;
  margin: 0 auto 2.6rem;
  line-height: 1.7;
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  gap: 1.8rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 1.8rem;
  backdrop-filter: blur(14px);
  text-align: left;
`;

const IconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(90, 110, 255, 0.18);
  border: 1px solid rgba(90, 110, 255, 0.25);
  margin-bottom: 0.9rem;

  svg {
    color: var(--primary-color);
    font-size: 18px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  margin: 0 0 0.6rem;
`;

const CardText = styled.p`
  margin: 0;
  color: var(--text-secondary-color);
  line-height: 1.7;
`;

const BulletList = styled.ul`
  margin: 0.85rem 0 0;
  padding-left: 1.1rem;
  color: var(--text-secondary-color);

  li {
    margin: 0.45rem 0;
    line-height: 1.6;
  }
`;

/* ============================================================
   HOW TO (Steps)
============================================================ */

const Steps = styled.div`
  max-width: 920px;
  margin: 0 auto;
  display: grid;
  gap: 1rem;
`;

const Step = styled.div`
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.1rem 1.2rem;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
`;

const StepNum = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-color);
`;

const StepTitle = styled.div`
  font-weight: 800;
  margin-bottom: 0.35rem;
`;

const StepText = styled.div`
  color: var(--text-secondary-color);
  line-height: 1.7;
`;

/* ============================================================
   CTA STRIP
============================================================ */

const CTASection = styled.section`
  padding: 3.5rem 10% 1rem;
`;

const CTABlock = styled.div`
  max-width: 980px;
  margin: 0 auto;
  border-radius: 22px;
  padding: 2.2rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  backdrop-filter: blur(14px);
  text-align: center;
`;

const CTATitle = styled.h2`
  margin: 0 0 0.7rem;
  font-size: 2rem;
`;

const CTAText = styled.p`
  margin: 0 auto 1.4rem;
  color: var(--text-secondary-color);
  max-width: 820px;
  line-height: 1.7;
`;

const LinkRow = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 0.9rem;
  flex-wrap: wrap;
`;

const SmallLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.75rem 1.05rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 800;
  color: var(--text-secondary-color);
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  transition: 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
  }
`;

/* ============================================================
   FAQ
============================================================ */

const FAQWrap = styled.section`
  padding: 3.5rem 10%;
`;

const FAQTitle = styled.h2`
  text-align: center;
  font-size: 2.3rem;
  margin: 0 0 2rem;
`;

const FAQBox = styled.div`
  max-width: 860px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid var(--border-color);
  padding: 1.1rem 0;
`;

const Q = styled.h3`
  font-size: 1.15rem;
  margin: 0 0 0.5rem;
`;

const A = styled.p`
  margin: 0;
  color: var(--text-secondary-color);
  line-height: 1.7;
`;

/* ============================================================
   DISCLAIMER
============================================================ */

const FinePrint = styled.p`
  max-width: 980px;
  margin: 2rem auto 0;
  padding: 0 10%;
  color: var(--text-secondary-color);
  font-size: 0.95rem;
  line-height: 1.7;
  opacity: 0.9;
`;

/* ============================================================
   MAIN
============================================================ */

const RewardsPage = () => {
  // SEO slug requested by you
  const canonicalUrl = "https://www.trendgenie.io/tgen-airdrop-live";

  const tgenContract = "0xdD3f2d1B0EC5ea81CDf70e79C713D35EF8d530E1";
  const bscscanLink = `https://bscscan.com/token/${tgenContract}`;
  const dexScreenerLink =
    "https://dexscreener.com/bsc/0xb6d72ccd34928314228fa72a8e80800dd9907795";

  const airdropSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Trend Genie (TGEN) Airdrop | Join the Ecosystem and Earn Daily Crypto Rewards",
    url: canonicalUrl,
    description:
      "Join the Trend Genie (TGEN) airdrop. Sign up with your EVM wallet and earn TGEN through daily check-ins, daily spin, social tasks, and referrals. TGEN is live on BNB Smart Chain (BEP-20). Snapshot date is TBA.",
    publisher: { "@type": "Organization", name: "Trend Genie", url: "https://www.trendgenie.io" },
  };

  return (
    <Page>
      <Helmet>
        <title>Trend Genie (TGEN) Airdrop | Join the Ecosystem & Earn Daily Crypto Rewards</title>
        <meta
          name="description"
          content="Join the Trend Genie Airdrop. Sign up with your EVM wallet to earn TGEN via daily check-ins, daily spin, social tasks, and referrals. TGEN is live and tradable on BNB Smart Chain (BEP-20). Start at social.trendgenie.io."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Trend Genie (TGEN) Airdrop is Live" />
        <meta
          property="og:description"
          content="Wallet signup. Earn TGEN through daily participation. Snapshot date TBA. Live on BNB Smart Chain (BEP-20)."
        />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(airdropSchema)}</script>
      </Helmet>

      {/* HERO */}
      <Hero>
        <HeroInner>
          <div>
            <Kicker>SocialFi airdrop</Kicker>

            <Title initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
              Unlock SocialFi: The Trend Genie Airdrop is Live
            </Title>

            <Subtitle initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              Earn <strong>$TGEN</strong> through real activity inside Trend Genie Social.
              Token is live on <strong>BNB Smart Chain</strong> (BEP-20) and tradable on DEX.
              Snapshot date is <strong>TBA</strong>.
            </Subtitle>

            <HeroCTA>
              <PrimaryButton href="https://social.trendgenie.io" target="_blank" rel="noreferrer">
                <FaWallet /> Connect Wallet & Join Airdrop
              </PrimaryButton>

              <SecondaryButton href="https://www.geckoterminal.com/bsc/pools/0xb6d72ccd34928314228fa72a8e80800dd9907795" target="_blank" rel="noreferrer">
                <FaLink /> View Chart on DEX
              </SecondaryButton>

              <SecondaryButton href="https://social.trendgenie.io/dapp" target="_blank" rel="noreferrer">
                <FaCoins /> Buy TGEN
              </SecondaryButton>
            </HeroCTA>
          </div>

          <HeroRight>
            <StatTitle>Quick details</StatTitle>
            <StatText>
              Network: <strong>BNB Smart Chain</strong> (BEP-20)
              <br />
              Token: <strong>TGEN</strong>
              <br />
              Contract: <Mono>{tgenContract}</Mono>
            </StatText>

            <StatGrid>
              <StatRow>
                <StatLabel>Status</StatLabel>
                <StatValue>
                  <StatusPill>Live</StatusPill>
                </StatValue>
              </StatRow>
              <StatRow>
                <StatLabel>Snapshot</StatLabel>
                <StatValue>TBA</StatValue>
              </StatRow>
              <StatRow>
                <StatLabel>Signup</StatLabel>
                <StatValue>EVM Wallet Login</StatValue>
              </StatRow>
            </StatGrid>
          </HeroRight>
        </HeroInner>
      </Hero>

      {/* INTRO / VALUE PROP */}
      <Section>
        <SectionTitle>What is the Trend Genie Ecosystem?</SectionTitle>
        <SectionDesc>
          Trend Genie is a crypto focused ecosystem built around community, utility, and daily participation.
          Trend Genie Social is where users connect and stay active, while $TGEN powers the experience on chain.
        </SectionDesc>

        <Grid>
          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaBolt /></IconWrap>
            <CardTitle>Built for real users</CardTitle>
            <CardText>
              This is not a one time form. Your allocation potential grows with consistent participation inside the platform.
              If you show up, you get counted.
            </CardText>
          </Card>

          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaCoins /></IconWrap>
            <CardTitle>Token is live and tradable</CardTitle>
            <CardText>
              $TGEN is already deployed on BNB Smart Chain (BEP-20) and available on decentralized markets.
              The airdrop is about distribution through participation, not promises.
            </CardText>
          </Card>

          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaUserCheck /></IconWrap>
            <CardTitle>Wallet based identity</CardTitle>
            <CardText>
              Accounts are created through wallet login. No passwords. Your wallet ties activity to one identity for fair tracking.
            </CardText>
          </Card>
        </Grid>
      </Section>

      {/* HOW TO PARTICIPATE */}
      <Section>
        <SectionTitle>How to earn your $TGEN allocation</SectionTitle>
        <SectionDesc>
          Start in minutes. Earn more by staying consistent. The more you interact, the stronger your allocation potential.
        </SectionDesc>

        <Steps>
          <Step>
            <StepNum>1</StepNum>
            <div>
              <StepTitle>Connect and sign up</StepTitle>
              <StepText>
                Visit <strong>social.trendgenie.io</strong> and create your account using an EVM compatible wallet.
                Supports MetaMask, Trust Wallet, and other EVM wallets.
              </StepText>
            </div>
          </Step>

          <Step>
            <StepNum>2</StepNum>
            <div>
              <StepTitle>Daily check-ins and daily spin</StepTitle>
              <StepText>
                Check in every 24 hours and use the daily spin. Consistency is the easiest way to build momentum.
              </StepText>
            </div>
          </Step>

          <Step>
            <StepNum>3</StepNum>
            <div>
              <StepTitle>Post and interact</StepTitle>
              <StepText>
                Post updates, like, comment, and engage with community content. Real interactions matter more than empty spam.
              </StepText>
            </div>
          </Step>

          <Step>
            <StepNum>4</StepNum>
            <div>
              <StepTitle>Referrals</StepTitle>
              <StepText>
                Invite users through your referral link. Quality referrals who participate consistently strengthen your results.
              </StepText>
            </div>
          </Step>
        </Steps>
      </Section>

      {/* TOKEN DETAILS */}
      <Section>
        <SectionTitle>$TGEN token details</SectionTitle>
        <SectionDesc>
          Built on BNB Smart Chain (BEP-20) for fast transactions and low fees. Snapshot and distribution timing will be announced as the campaign progresses.
        </SectionDesc>

        <Grid>
          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaCoins /></IconWrap>
            <CardTitle>Market status</CardTitle>
            <CardText>
              $TGEN is live and tradable on DEX. The airdrop allocation is earned through participation inside Trend Genie Social.
            </CardText>
          </Card>

          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaRegClock /></IconWrap>
            <CardTitle>Snapshot and distribution</CardTitle>
            <CardText>
              Snapshot date is <strong>TBA</strong>. Distribution details will follow after the snapshot announcement.
            </CardText>
          </Card>

          <Card initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}>
            <IconWrap><FaShieldAlt /></IconWrap>
            <CardTitle>Verify safely</CardTitle>
            <CardText>
              Always verify the contract address and links. Never share your seed phrase or private key.
            </CardText>
            <CardText style={{ marginTop: "0.75rem" }}>
              Contract: <Mono>{tgenContract}</Mono>
            </CardText>
          </Card>
        </Grid>
      </Section>

      {/* REFERRALS */}
      <Section>
        <SectionTitle>Invite friends, grow faster</SectionTitle>
        <SectionDesc>
          Referrals reward real community growth. Share your dashboard referral link and invite users who will actively participate.
          Anti abuse checks may apply to keep distribution fair.
        </SectionDesc>

        <CTASection style={{ paddingTop: 0 }}>
          <CTABlock>
            <CTATitle>Get your referral link</CTATitle>
            <CTAText>
              Your unique referral link is available inside your Trend Genie Social dashboard after wallet signup.
            </CTAText>

            <PrimaryButton href="https://social.trendgenie.io" target="_blank" rel="noreferrer">
              <FaLink /> Get My Referral Link
            </PrimaryButton>
          </CTABlock>
        </CTASection>
      </Section>

      {/* FAQ */}
      <FAQWrap>
        <FAQTitle>Frequently asked questions</FAQTitle>
        <FAQBox>
          <FAQItem>
            <Q>Is $TGEN already listed and tradable?</Q>
            <A>
              Yes. $TGEN is deployed on BNB Smart Chain (BEP-20) and is tradable on decentralized exchanges.
            </A>
          </FAQItem>

          <FAQItem>
            <Q>How do I join the airdrop?</Q>
            <A>
              Go to <strong>social.trendgenie.io</strong> and connect your EVM wallet to create an account. No passwords required.
            </A>
          </FAQItem>

          <FAQItem>
            <Q>What increases my eligibility?</Q>
            <A>
              Daily check-ins, posting, interactions, daily spin, and referrals. Consistency and real engagement are prioritized.
            </A>
          </FAQItem>

          <FAQItem>
            <Q>When is the snapshot and distribution?</Q>
            <A>
              Snapshot date is currently TBA. Distribution timing will be announced after the snapshot update.
            </A>
          </FAQItem>

          <FAQItem>
            <Q>Which wallets are supported?</Q>
            <A>
              MetaMask, Trust Wallet, and most EVM compatible wallets are supported.
            </A>
          </FAQItem>

          <FAQItem>
            <Q>Any security tips?</Q>
            <A>
              Trend Genie will never ask for your seed phrase or private key. Always double check the domain before connecting your wallet.
            </A>
          </FAQItem>
        </FAQBox>
      </FAQWrap>

      <FinePrint>
        Eligibility rules and scoring can be adjusted to reduce abuse and protect fair distribution. Participation does not guarantee an allocation.
        This page is informational and not financial advice.
      </FinePrint>
    </Page>
  );
};

export default RewardsPage;

